import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  BottomNavigation,
  BottomNavigationAction,
  Box,
  Badge
} from '@mui/material';
import {
  MOBILE_BOTTOM_NAV_HEIGHT,
  MOBILE_BOTTOM_NAV_PADDING,
  SAFE_AREA_BOTTOM,
  mobileBottomNavTotalHeight
} from './pageLayout/constants';
import {
  Dashboard as DashboardIcon,
  ShoppingCart as OrdersIcon,
  Restaurant as MenusIcon,
  Inventory as InventoryIcon,
  Analytics as AnalyticsIcon
} from '@mui/icons-material';

interface MobileBottomNavigationProps {
  lowStockCount?: number;
  expiryAlertCount?: number;
  totalAlertCount?: number;
}

const easeOut = 'cubic-bezier(0.23, 1, 0.32, 1)';

const MobileBottomNavigation: React.FC<MobileBottomNavigationProps> = ({
  lowStockCount = 0,
  expiryAlertCount = 0,
  totalAlertCount = 0
}) => {
  const navigate = useNavigate();
  const location = useLocation();

  const alertCount = totalAlertCount > 0 ? totalAlertCount : lowStockCount;

  const navigationItems = [
    { value: '/dashboard', icon: <DashboardIcon />, label: '대시보드' },
    { value: '/orders', icon: <OrdersIcon />, label: '주문관리' },
    { value: '/menus', icon: <MenusIcon />, label: '메뉴관리' },
    {
      value: '/inventory',
      icon:
        alertCount > 0 ? (
          <Badge badgeContent={alertCount} color="error">
            <InventoryIcon />
          </Badge>
        ) : (
          <InventoryIcon />
        ),
      label: '재고관리'
    },
    { value: '/analytics', icon: <AnalyticsIcon />, label: '통계분석' },
  ];

  return (
    <Box
      sx={{
        background: 'rgba(255, 255, 255, 0.72)',
        backdropFilter: 'blur(20px) saturate(180%)',
        WebkitBackdropFilter: 'blur(20px) saturate(180%)',
        borderTop: '1px solid rgba(255, 255, 255, 0.4)',
        boxShadow: '0 12px 32px rgba(25,35,45,0.14)',
        width: '100%',
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 1300,
        // main의 paddingBottom과 같은 값이어야 콘텐츠와 탭바 사이 간격이 기기별로 달라지지 않는다.
        height: mobileBottomNavTotalHeight,
        paddingBottom: `calc(${MOBILE_BOTTOM_NAV_PADDING} + ${SAFE_AREA_BOTTOM})`,
        '@media (prefers-reduced-transparency: reduce)': {
          background: '#ffffff',
          backdropFilter: 'none',
          WebkitBackdropFilter: 'none',
          borderTop: '1px solid rgba(0, 0, 0, 0.12)'
        }
      }}
    >
      <BottomNavigation
        value={location.pathname}
        onChange={(_, newValue) => {
          if (newValue === '/dashboard') {
            sessionStorage.setItem('skipMultiStoreRedirectAt', String(Date.now()));
            navigate(newValue, { state: { skipMultiStoreRedirect: true } });
            return;
          }
          navigate(newValue);
        }}
        sx={{
          height: MOBILE_BOTTOM_NAV_HEIGHT,
          background: 'transparent',
          '& .MuiBottomNavigationAction-root': {
            minWidth: '20%',
            maxWidth: '100%',
            padding: '6px 0 8px',
            color: 'text.secondary',
            fontWeight: 500,
            transition: `color 100ms ${easeOut}, transform 100ms ${easeOut}`,
            '&:active': {
              transform: 'scale(0.97)'
            },
            '&.Mui-selected': {
              color: 'primary.main',
              fontWeight: 600,
              '& .MuiSvgIcon-root': {
                transform: 'translateY(-2px)'
              },
              '&::after': {
                content: '""',
                position: 'absolute',
                bottom: '10px',
                left: '50%',
                transform: 'translateX(-50%)',
                width: '4px',
                height: '4px',
                borderRadius: '50%',
                backgroundColor: 'primary.main'
              }
            },
            '& .MuiSvgIcon-root': {
              fontSize: '22px',
              transition: `transform 100ms ${easeOut}`
            },
            '& .MuiBottomNavigationAction-label': {
              // 테마 0.75rem 유지 — 0.6rem로 줄이면 320px에서 가독성만 나빠지고 줄바꿈 이득은 없음
              opacity: 0.75,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              lineHeight: 1.2,
              letterSpacing: '-0.01em',
              transition: `opacity 100ms ${easeOut}`
            },
            '&.Mui-selected .MuiBottomNavigationAction-label': {
              opacity: 1,
              fontWeight: 600
            }
          }
        }}
      >
        {navigationItems.map((item) => (
          <BottomNavigationAction
            key={item.value}
            value={item.value}
            icon={item.icon}
            label={item.label}
            showLabel={true}
          />
        ))}
      </BottomNavigation>
    </Box>
  );
};

export default MobileBottomNavigation;
